import React, {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";

import isHotkey from "is-hotkey";
import {
  Editable,
  withReact,
  useSlate,
  Slate,
  useSelected,
  useFocused,
  ReactEditor,
  useEditor,
} from "slate-react";
import { Editor, Transforms, createEditor, Node, Range } from "slate";
import { withHistory } from "slate-history";
import { useForm, useWatch } from "react-hook-form";
import nanoId from "nanoid";

import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  FormatBoldIcon,
  FormatItalicIcon,
  FormatUnderlineIcon,
  SaveIcon,
} from "../icons";

import useTranslations from "../../hooks/useTranslations";

import Modal from "../wombat-ui/Modal";
import Input from "../wombat-ui/Input";
import Radio from "../wombat-ui/Radio";
import { generatePdfDocument } from "../pdf/Contract/ContractPDFBuilder";
import { useContractEditorContext } from "./ContractEditorContext";
import Checkbox from "../wombat-ui/Checkbox";

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
};

const LIST_TYPES = ["bulleted-list"];
const ALIGNEMENT_TYPES = ["leftAlign", "centerAlign", "rightAlign"];

export type ContractVariable = {
  label: string;
  value?: string;
};

type ContractVariableFormModalProps = {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
};
const ContractVariableFormModal: React.FC<ContractVariableFormModalProps> = ({
  visible,
  onClose,
  onSubmit,
}) => {
  const t = useTranslations();
  const { register, handleSubmit, errors, control } = useForm<ContractVariable>(
    {
      defaultValues: {
        label: "",
      },
    }
  );

  const label = useWatch({
    name: "label",
    control,
    defaultValue: "nom_de_votre_variable",
  });

  const _onSubmit = useCallback(
    (data) => {
      onSubmit(data);
      onClose();
    },
    [onSubmit]
  );

  return (
    <Modal visible={visible}>
      <>
        <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className="w-full mt-3 sm:mt-0 sm:ml-4">
              <h3
                className="text-lg font-medium leading-6 text-gray-900"
                id="modal-headline"
              >
                {t("contracts.createContractVariable")}
              </h3>
              <form
                className="mt-3 space-y-4"
                id="createContractVariableForm"
                onSubmit={handleSubmit(_onSubmit)}
              >
                <div>
                  <Input
                    label={
                      t("contracts.contractVariableQuestionTitle") as string
                    }
                    autoComplete="off"
                    error={errors?.label}
                    name={`label`}
                    ref={register({ required: true })}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {t("contracts.variableWillBeAvailableDescription")}
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    {t("contracts.toAccessVarHint")}{" "}
                    <code className="text-primary-600">#{label}</code>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
          {/* MIGRATE WOMBAT UI */}
          <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
            <button
              type="submit"
              form="createContractVariableForm"
              className="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-primary-600 hover:bg-primary-500 focus:outline-none focus:border-primary-700 focus:shadow-outline-primary sm:text-sm sm:leading-5"
            >
              {t("forms.actions.add")}
            </button>
          </span>
          <span className="flex w-full mt-3 rounded-md shadow-sm sm:mt-0 sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-gray-700 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5"
            >
              {t("forms.actions.cancel")}
            </button>
          </span>
        </div>
      </>
    </Modal>
  );
};

export type SigneeType = "administrator" | "referent" | "talent";

export const defaultSigneesAvailable: SigneeType[] = [
  "administrator",
  "referent",
  "talent",
];

type SigneeFormModalProps = {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  availableSigneeType: SigneeType[];
};
const SigneeFormModal: React.FC<SigneeFormModalProps> = ({
  visible,
  onClose,
  onSubmit,
  availableSigneeType,
}) => {
  const t = useTranslations();
  const { register, handleSubmit } = useForm<{
    signee: string;
  }>({
    defaultValues: {
      signee: availableSigneeType[0],
    },
  });

  const _onSubmit = useCallback((data) => {
    onSubmit(data);
    onClose();
  }, []);

  return (
    <Modal visible={visible}>
      <>
        <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className="w-full mt-3 sm:mt-0 sm:ml-4">
              <h3
                className="text-lg font-medium leading-6 text-gray-900"
                id="modal-headline"
              >
                {t("contracts.addSigneeTitle")}
              </h3>
              <p className="mt-1 text-sm leading-5 text-gray-600">
                {t("contracts.addSigneeDescription")}
              </p>
              <form
                className="mt-4 space-y-4"
                id="createSigneeForm"
                onSubmit={handleSubmit(_onSubmit)}
              >
                <div className="flex flex-col space-y-4">
                  {availableSigneeType.map((signee) => (
                    <Radio
                      label={t(`contracts.${signee}Signee`) as string}
                      name={`signee`}
                      value={signee}
                      ref={register}
                      key={signee}
                    />
                  ))}
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
          {/* MIGRATE WOMBAT UI */}
          <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
            <button
              type="submit"
              form="createSigneeForm"
              className="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-primary-600 hover:bg-primary-500 focus:outline-none focus:border-primary-700 focus:shadow-outline-primary sm:text-sm sm:leading-5"
            >
              {t("forms.actions.add")}
            </button>
          </span>
          <span className="flex w-full mt-3 rounded-md shadow-sm sm:mt-0 sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-gray-700 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5"
            >
              {t("forms.actions.cancel")}
            </button>
          </span>
        </div>
      </>
    </Modal>
  );
};

const VariableElement = ({ attributes, children, element }) => {
  const { variables } = useContractEditorContext();
  const editor = useEditor();
  const selected = useSelected();
  const focused = useFocused();

  useEffect(() => {
    const contextVariable = variables.find((v) => v.label === element.variable);

    if (contextVariable?.value) {
      const path = ReactEditor.findPath(editor, element);

      Transforms.setNodes(
        editor,
        { ...element, value: contextVariable.value },
        { at: path }
      );
    }
  }, [variables, element, editor]);

  return (
    <span
      {...attributes}
      className={`px-2 py-1 text-primary-600 rounded-md ${
        selected && focused ? "shadow bg-primary-100" : "bg-gray-50"
      }`}
    >
      {element.value ? element.value : children}
    </span>
  );
};

const SignaturesElement = ({ attributes, children, element }) => {
  const t = useTranslations();
  const selected = useSelected();
  const focused = useFocused();

  useEffect(() => element.onRemove, [element]);

  return (
    <div {...attributes}>
      <div
        contentEditable={false}
        style={{ height: "3cm" }}
        className={`w-full grid grid-cols-3 gap-4 font-sans ${
          selected && focused ? "shadow-outline-primary" : ""
        }`}
      >
        <div className="flex items-center justify-center px-8 py-6 m-4 text-xs font-semibold leading-5 bg-gray-100 border border-gray-200 rounded-lg text-primary-500">
          {t(`contracts.signee.${element.label}`)}
        </div>
      </div>
      {children}
    </div>
  );
};

const Portal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

export type ContractEditorState = {
  initialState: Node[];
  variables: ContractVariable[];
  title: string;
  signatories: SigneeType[];
};

type ContractEditorProps = {
  state?: ContractEditorState;
  editable?: boolean;
  onSubmit?: (
    data: ContractEditorState & { signatories: SigneeType[] }
  ) => void;
  onContractGeneration?: (
    b64PDF: string,
    filename: string,
    withDocusign: boolean
  ) => void;
};

const ContractEditor: React.FC<ContractEditorProps> = ({
  state = {
    initialState: defaultState,
    variables: [],
    title: "",
    signatories: defaultSigneesAvailable,
  },
  editable = false,
  onSubmit = () => {},
  onContractGeneration,
}) => {
  const { initialState, title, signatories } = state;
  const { variables, addVariable } = useContractEditorContext();
  const t = useTranslations();

  const [withDocusign, setWithDocusign] = useState(true);

  const [variableModalOpen, setVariableModalOpen] = useState(false);
  const [signeeModalOpen, setSigneeModalOpen] = useState(false);
  const handleVariableModalClose = useCallback(
    () => setVariableModalOpen(false),
    []
  );
  const handleSigneeModalClose = useCallback(
    () => setSigneeModalOpen(false),
    []
  );

  const ref = useRef<HTMLDivElement | null>();
  const [value, setValue] = useState<Node[]>(initialState);
  const [target, setTarget] = useState<Range | undefined>();
  const [index, setIndex] = useState(0);
  const [search, setSearch] = useState("");

  const [signeesAvailable, setSigneesAvailable] = useState<SigneeType[]>(
    signatories
  );

  const { register, handleSubmit, trigger, getValues } = useForm<{
    title: string;
  }>({
    defaultValues: {
      title,
    },
  });

  const _onSubmit = useCallback(
    (data) => {
      const state: ContractEditorState & { signatories: SigneeType[] } = {
        initialState: value,
        title: data.title,
        variables,
        signatories: defaultSigneesAvailable.filter(
          (s) => signeesAvailable.indexOf(s) === -1
        ),
      };

      onSubmit(state);
    },
    [value, variables, signeesAvailable]
  );

  const _onManualSave = useCallback(async () => {
    const validated = await trigger("title");

    if (validated) _onSubmit({ title: getValues("title") });
  }, [getValues, _onSubmit]);

  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => {
    return <Leaf {...props} />;
  }, []);
  const editor = useMemo(
    () => withContractEditor(withHistory(withReact(createEditor()))),
    []
  );

  const chars = variables
    .filter(({ label }) => label.toLowerCase().startsWith(search.toLowerCase()))
    .slice(0, 10);

  const onKeyDown = useCallback(
    (event) => {
      if (target) {
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            const prevIndex = index >= chars.length - 1 ? 0 : index + 1;
            setIndex(prevIndex);
            break;
          case "ArrowUp":
            event.preventDefault();
            const nextIndex = index <= 0 ? chars.length - 1 : index - 1;
            setIndex(nextIndex);
            break;
          case "Tab":
          case "Enter":
            event.preventDefault();
            Transforms.select(editor, target);
            insertVariable(editor, chars[index].label);
            setTarget(null);
            break;
          case "Escape":
            event.preventDefault();
            setTarget(null);
            break;
        }
      }
      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event as any)) {
          event.preventDefault();
          const mark = HOTKEYS[hotkey];
          toggleMark(editor, mark);
        }
      }
    },
    [index, search, target]
  );

  useEffect(() => {
    if (target && chars.length > 0) {
      const el = ref.current;
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();
      el.style.top = `${rect.top + window.pageYOffset + 24}px`;
      el.style.left = `${rect.left + window.pageXOffset}px`;
    }
  }, [chars.length, editor, index, search, target]);

  const insertSignatureBlock = useCallback(
    ({ signee }) => {
      const text = { text: "" };
      setSigneesAvailable((state) => state.filter((s) => s !== signee));

      const signatures = {
        type: "signatures",
        label: signee,
        docusignTag: `__Docusign__${signee}`,
        onRemove: () => setSigneesAvailable((state) => [...state, signee]),
        signatories: {
          talent: false,
          referent: false,
          administrator: false,
          [signee]: true,
        },
        children: [text],
      };

      Transforms.insertNodes(editor, signatures);
    },
    [editor]
  );

  return (
    <>
      {onContractGeneration && (
        // MIGRATE WOMBAT UI
        <div className="flex items-baseline justify-between px-4 py-2 bg-white rounded shadow-md">
          <Checkbox
            onChange={(e) => setWithDocusign(e.target.checked)}
            label={t("contracts.signDigitally") as string}
            defaultChecked={true}
          />
          <span className="flex rounded-md shadow-sm sm:ml-3 sm:w-auto">
            <button
              type="button"
              onClick={() =>
                generatePdfDocument(
                  { name: `${state.title}.pdf`, sections: value as any },
                  (b64PDF, filename) =>
                    onContractGeneration(b64PDF, filename, withDocusign)
                )
              }
              className="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-primary-600 hover:bg-primary-500 focus:outline-none focus:border-primary-700 focus:shadow-outline-primary sm:text-sm sm:leading-5"
            >
              {t("forms.actions.save")}
            </button>
          </span>
        </div>
      )}
      {editable && (
        <>
          {variableModalOpen && (
            <ContractVariableFormModal
              visible={variableModalOpen}
              onClose={handleVariableModalClose}
              onSubmit={addVariable}
            />
          )}
          {signeeModalOpen && (
            <SigneeFormModal
              visible={signeeModalOpen}
              onClose={handleSigneeModalClose}
              onSubmit={insertSignatureBlock}
              availableSigneeType={signeesAvailable}
            />
          )}
        </>
      )}
      <form
        className="min-h-lg"
        id="contractForm"
        onSubmit={handleSubmit(_onSubmit)}
      >
        {editable && (
          <div className="flex justify-center pb-4">
            <div className="flex items-baseline space-x-4 w-A4">
              <div className="flex-grow">
                <Input
                  name={"title"}
                  placeholder={title}
                  disabled={!editable}
                  ref={register({ required: true })}
                />
              </div>
              <div>
                {/* MIGRATE WOMBAT UI */}
                <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
                  <button
                    id="submitContractTemplateButton"
                    type="submit"
                    className="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-primary-600 hover:bg-primary-500 focus:outline-none focus:border-primary-700 focus:shadow-outline-primary sm:text-sm sm:leading-5"
                  >
                    {t("forms.actions.save")}
                  </button>
                </span>
              </div>
            </div>
          </div>
        )}
        <Slate
          editor={editor}
          value={value}
          onChange={(value) => {
            setValue(value);

            const { selection } = editor;

            if (selection && Range.isCollapsed(selection)) {
              const [start] = Range.edges(selection);
              const wordBefore = Editor.before(editor, start, { unit: "word" });
              const before = wordBefore && Editor.before(editor, wordBefore);
              const beforeRange = before && Editor.range(editor, before, start);
              const beforeText =
                beforeRange && Editor.string(editor, beforeRange);
              const beforeMatch = beforeText && beforeText.match(/^#(\w+)$/);
              const after = Editor.after(editor, start);
              const afterRange = Editor.range(editor, start, after);
              const afterText = Editor.string(editor, afterRange);
              const afterMatch = afterText.match(/^(\s|$)/);

              if (beforeMatch && afterMatch) {
                setTarget(beforeRange);
                setSearch(beforeMatch[1]);
                setIndex(0);
                return;
              }
            }

            setTarget(null);
          }}
        >
          <div className="flex flex-col items-center w-full">
            {editable && (
              <div className="sticky z-10 flex flex-col-reverse w-full px-4 py-2 space-x-6 bg-white rounded shadow-md top-1 max-w-A4 sm:flex-row sm:justify-center">
                <div className="flex justify-between space-x-6 sm:justify-start">
                  <IconButton
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      _onManualSave();
                    }}
                  >
                    <SaveIcon iconClassName="w-4 h-4" />
                  </IconButton>
                  <MarkButton
                    format="bold"
                    icon={<FormatBoldIcon iconClassName="w-4 h-4" />}
                  />
                  <MarkButton
                    format="italic"
                    icon={<FormatItalicIcon iconClassName="w-4 h-4" />}
                  />
                  <MarkButton
                    format="underline"
                    icon={<FormatUnderlineIcon iconClassName="w-4 h-4" />}
                  />
                  <BlockButton
                    format="leftAlign"
                    icon={<AlignLeftIcon iconClassName="w-4 h-4" />}
                  />
                  <BlockButton
                    format="centerAlign"
                    icon={<AlignCenterIcon iconClassName="w-4 h-4" />}
                  />
                  <BlockButton
                    format="rightAlign"
                    icon={<AlignRightIcon iconClassName="w-4 h-4" />}
                  />
                  <MarkButton
                    format="headingOne"
                    icon={<span className="text-xl font-bold">1</span>}
                  />
                  <MarkButton
                    format="headingTwo"
                    icon={<span className="text-xl font-bold">2</span>}
                  />
                  {/* <BlockButton
              format="bulleted-list"
              icon={<FormatListBulletIcon iconClassName="w-4 h-4" />}
            /> */}
                </div>
                <div className="flex space-x-2 space-y-2 max-w-A4 sm:flex-grow sm:justify-end sm:space-y-0">
                  <div>
                    {/* MIGRATE WOMBAT UI */}
                    <button
                      type="button"
                      className="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-gray-700 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5 disabled:text-gray-400 disabled:opacity-75 disabled:cursor-not-allowed"
                      onClick={() => setSigneeModalOpen(true)}
                      disabled={signeesAvailable.length === 0}
                    >
                      {t("contracts.actions.addSignee")}
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-primary-600 hover:bg-primary-500 focus:outline-none focus:border-primary-700 focus:shadow-outline-primary sm:text-sm sm:leading-5"
                      onClick={() => setVariableModalOpen(true)}
                    >
                      {t("contracts.actions.createVariable")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-center w-full py-6">
              <div className="w-full font-serif text-sm bg-white rounded-sm shadow max-w-A4 min-h-A4 p-A4 md:text-base">
                <Editable
                  renderElement={renderElement}
                  renderLeaf={renderLeaf}
                  placeholder={t("contracts.startEditing") as string}
                  spellCheck
                  readOnly={!editable}
                  autoFocus
                  onKeyDown={onKeyDown}
                />
                {target && chars.length > 0 && (
                  //  MIGRATE WOMBAT UI
                  <Portal>
                    <div
                      ref={ref}
                      className="absolute block mt-1 bg-white rounded-md shadow-lg min-w-xs max-h-56"
                      style={{
                        top: "-9999px",
                        left: "-9999px",
                      }}
                    >
                      <ul
                        tabIndex={-1}
                        role="listbox"
                        className="py-1 overflow-auto text-base leading-6 bg-white rounded-md shadow-xs max-h-56 focus:outline-none sm:text-sm sm:leading-5"
                      >
                        {chars.map((char, i) => (
                          <li
                            key={char.label}
                            role="option"
                            className={`relative py-1 pl-2 cursor-default select-none pr-9 ${
                              i === index
                                ? "bg-primary-600 text-white"
                                : "text-gray-900"
                            }`}
                          >
                            {char.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Portal>
                )}
              </div>
            </div>
          </div>
        </Slate>
      </form>
    </>
  );
};

const toggleBlock = (editor, format) => {
  const isAlignement = ALIGNEMENT_TYPES.includes(format);
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  if (isAlignement)
    Transforms.setNodes(editor, {
      alignment: format,
    });
  else {
    Transforms.unwrapNodes(editor, {
      match: (n) => LIST_TYPES.includes(n.type as string),
      split: true,
    });

    Transforms.setNodes(editor, {
      type: isActive ? "paragraph" : isList ? "list-item" : format,
    });

    if (!isActive && isList) {
      const block = { type: format, children: [] };
      Transforms.wrapNodes(editor, block);
    }
  }
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => n.type === format || n?.alignment === format,
  });

  return !!match;
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const Element = (props) => {
  const { attributes, children, element } = props;

  switch (element.type) {
    case "bulleted-list":
      return <ul {...attributes}>{children}</ul>;
    case "list-item":
      return (
        <li className="list-disc" {...attributes}>
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol className="list-decimal" {...attributes}>
          {children}
        </ol>
      );
    case "variable":
      return <VariableElement {...props} />;
    case "signatures":
      return <SignaturesElement {...props} />;
    default:
      switch (element?.alignment) {
        case "centerAlign":
          return (
            <p align="center" {...attributes}>
              {children}
            </p>
          );
        case "rightAlign":
          return (
            <p align="right" {...attributes}>
              {children}
            </p>
          );
        default:
          return (
            <p align="left" {...attributes}>
              {children}
            </p>
          );
      }
  }
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  if (leaf.headingOne) {
    children = <span className="text-2xl font-extrabold">{children}</span>;
  } else if (leaf.headingTwo) {
    children = <span className="text-xl font-bold">{children}</span>;
  }

  return <span {...attributes}>{children}</span>;
};

// MIGRATE WOMBAT UI
type IconButtonProps = { active?: boolean } & Omit<
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  "className"
>;
const IconButton: React.FC<IconButtonProps> = React.memo(
  ({ active = false, children, ...props }) => (
    <span className={`flex items-center justify-center`}>
      <button
        {...props}
        className={active ? "text-gray-900" : "text-gray-400 "}
      >
        {children}
      </button>
    </span>
  )
);

type BlockButtonProps = {
  format: any;
  icon: ReactNode;
};

const BlockButton: React.FC<BlockButtonProps> = ({ format, icon }) => {
  const editor = useSlate();

  return (
    <IconButton
      active={isBlockActive(editor, format)}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
      type="button"
    >
      {icon}
    </IconButton>
  );
};

type MarkButtonProps = {
  format: any;
  icon: ReactNode;
};

const MarkButton: React.FC<MarkButtonProps> = ({ format, icon }) => {
  const editor = useSlate();
  return (
    <IconButton
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
      type="button"
      active={isMarkActive(editor, format)}
    >
      {icon}
    </IconButton>
  );
};

const withContractEditor = (editor) => {
  const { isInline, isVoid } = editor;

  editor.isInline = (element) => {
    return element.type === "variable" ? true : isInline(element);
  };

  editor.isVoid = (element) => {
    return element.type === "signatures" ? true : isVoid(element);
  };

  return editor;
};

const insertVariable = (editor, character) => {
  const variable = {
    type: "variable",
    id: nanoId(),
    variable: character,
    children: [{ text: character }],
  };
  Transforms.insertNodes(editor, variable, { select: true });
  Transforms.move(editor);
};

export const defaultState = [
  {
    type: "paragraph",
    children: [
      {
        text: "",
      },
    ],
  },
];

export default React.memo(ContractEditor);
